import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { Grid, TextField, Typography, FormControl, InputLabel, Select, MenuItem, FormHelperText } from '@mui/material';
import { useErrorInputs } from '../../hooks/useErrorInputs';
import taxResponsibilities from "../../data/taxResponsibilities.json";
import identificationTypes from "../../data/identificationTypes.json";
import { MultipleSelect } from '../others/MultipleSelect';
import '../../styles/styleInput/inputStyle.css';
import { useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';

export const LegalInformationForm = forwardRef(({ dto, handleInputChange, handleDirectInputChange }, ref) => {
    const { t } = useTranslation();
    const location = useLocation();
    const [errorInputs, handleErrorInput] = useErrorInputs({});
    const [isNitDisabled, setIsNitDisabled] = useState(false);

    useEffect(() => {
        if (location.state?.config?.data) {
            const { id } = location.state.config.data;

            if (id) {
                setIsNitDisabled(true);
            } else {
                setIsNitDisabled(false);
            }
        }
    }, [location.state]);

    const handleSelectObligation = (event) => {
        const { target: { value }, } = event;
        const formattedValue = Array.isArray(value) ? value : (typeof value === 'string' ? value.split(',') : []);
        console.log("Valor final de supplier_tax_level_code:", formattedValue);
        handleDirectInputChange('supplier_tax_level_code', formattedValue);
    };


    const validateField = (name, value) => {
        if (value === null || value === undefined || (typeof value === 'string' && value.trim() === '') || (Array.isArray(value) && value.length === 0)) {
            handleErrorInput(name, { isError: true, messageError: t("validation.requiredField") });
            return false;
        } else {
            handleErrorInput(name, { isError: false, messageError: '' });
            return true;
        }
    };

    useImperativeHandle(ref, () => ({
        validateForm,
    }));

    const requiredField = ['type_identification', 'business_name', 'society_type', 'company_type', 'supplier_tax_scheme', 'supplier_tax_level_code'];
    const validateForm = () => {
        let hasError = false;
        requiredField.forEach((field) => {
            const isValid = validateField(field, dto[field]);
            if (!isValid) hasError = true;
        });
        return !hasError;
    };

    return (
        <Grid container spacing={2} sx={{ mt: 2 }}>
            <Grid size={{ xs: 12}}>
                <Typography variant="h6">{t("suppliers.legalInformation")}</Typography>
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                    fullWidth
                    label={t("commonFields.nit")}
                    size="small"
                    name='identification'
                    onChange={handleInputChange}
                    value={dto.identification}
                    required
                    disabled={isNitDisabled}
                />
            </Grid>

            <Grid size={{xs:12, sm: 4}}>
                <TextField
                    fullWidth
                    name='identification_digit'
                    label={t("commonFields.verificationDigit")}
                    size="small"
                    value={dto.identification_digit}
                    onChange={handleInputChange}
                />
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
                <FormControl
                    fullWidth
                    size="small"
                    error={Boolean(errorInputs.type_identification?.isError)}
                    required
                >
                    <InputLabel>{t("commonFields.identificationType")}</InputLabel>
                    <Select
                        label={t("commonFields.identificationType")}
                        name="type_identification"
                        value={dto.type_identification}
                        onChange={handleInputChange}
                    >
                        {identificationTypes.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                                {option.label}
                            </MenuItem>
                        ))}
                    </Select>
                    {errorInputs.type_identification?.isError && (
                        <FormHelperText>{errorInputs.type_identification.messageError}</FormHelperText>
                    )}
                </FormControl>
            </Grid>


            <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                    fullWidth
                    size="small"
                    name='business_name'
                    label={t("commonFields.businessName")}
                    value={dto.business_name}
                    onChange={handleInputChange}
                    error={Boolean(errorInputs.business_name?.isError)}
                    helperText={errorInputs.business_name?.messageError || ''}
                    required
                />
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
                <FormControl
                    fullWidth
                    size='small'
                    error={Boolean(errorInputs.society_type?.isError)}
                    required
                >
                    <InputLabel>{t("commonFields.societyType")}</InputLabel>
                    <Select
                        label={t("commonFields.societyType")}
                        name='society_type'
                        value={dto.society_type}
                        onChange={handleInputChange}
                    >
                        <MenuItem value="S.A.S">S.A.S</MenuItem>
                        <MenuItem value="Limitada">Limitada</MenuItem>
                        <MenuItem value="Anónima">Anónima</MenuItem>
                        <MenuItem value="Otra">Otra</MenuItem>
                    </Select>
                    {errorInputs.society_type?.isError && (
                        <FormHelperText>{errorInputs.society_type.messageError}</FormHelperText>
                    )}
                </FormControl>
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
                <FormControl
                    fullWidth
                    size='small'
                    error={Boolean(errorInputs.company_type?.isError)}
                    required
                >
                    <InputLabel>{t("commonFields.companyType")}</InputLabel>
                    <Select
                        label={t("commonFields.companyType")}
                        name='company_type'
                        value={dto.company_type}
                        onChange={handleInputChange}
                    >
                        <MenuItem value="Privada">Privada</MenuItem>
                        <MenuItem value="Pública">Pública</MenuItem>
                        <MenuItem value="Mixta">Mixta</MenuItem>
                        <MenuItem value="Sin ánimo de lucro">Sin ánimo de lucro</MenuItem>
                    </Select>
                    {errorInputs.company_type?.isError && (
                        <FormHelperText>{errorInputs.company_type.messageError}</FormHelperText>
                    )}
                </FormControl>
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
                <FormControl
                    fullWidth size="small"
                    error={!!errorInputs.supplier_tax_scheme?.isError}
                    required>
                    <InputLabel>{t("commonFields.taxType")}</InputLabel>
                    <Select
                        label={t("commonFields.taxType")}
                        name="supplier_tax_scheme"
                        value={dto.supplier_tax_scheme}
                        onChange={handleInputChange}
                    >
                        <MenuItem value="01">IVA</MenuItem>
                        <MenuItem value="04">INC</MenuItem>
                        <MenuItem value="ZA">IVA e INC</MenuItem>
                        <MenuItem value="ZZ">No aplica</MenuItem>
                    </Select>
                    {errorInputs.supplier_tax_scheme?.isError && (
                        <FormHelperText>{errorInputs.supplier_tax_scheme.messageError}</FormHelperText>
                    )}
                </FormControl>
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
                <MultipleSelect
                    data={taxResponsibilities}
                    values={Array.isArray(dto.supplier_tax_level_code) ? dto.supplier_tax_level_code : []}
                    handleChange={handleSelectObligation}
                    label={t("commonFields.obligations")}
                />
            </Grid>
        </Grid>
    );
});
